import { AbbreviatedCarrierCodes, SharedBookingServices } from '@views/common/models';
import { PaymentStatus, PaymentType } from '@views/common/models/payment-types.enum';
import { Station } from '@vueling-ng/vy-cdk-searcher';
import { ItineraryTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/composed/basket';
import { CardFeaturedModel } from '@vueling-ng/vy-ds/src/lib/components/composed/card-featured';
import {
  AlertMessageAttrTypeEnum,
  AlertMessageSizeEnum,
  AlertMessageTypeEnum
} from '@vueling-ng/vy-ds/src/lib/components/globals/alert-message';
import { BreadcrumbItem } from '@vueling-ng/vy-ds/src/lib/components/globals/breadcrumb';
import { ButtonTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/button';
import { LoaderConfig } from '@vueling-ng/vy-ds/src/lib/components/globals/loaders/loader';
import { SpinnerColorEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/loaders/spinner';
import { ModalAction, ModalConfig } from '@vueling-ng/vy-ds/src/lib/components/globals/modal';
import { GenericModalComponent } from 'app/shared/generic-modal/generic-modal.component';
import { internalRoutes } from 'environments/common/internal-routes';
import { AvailableWebsites, redirectRoutes, redirectRoutes_Legacy } from 'environments/common/redirect-routes';

export interface BookingDetails {
  paxAmount: number;
  recordLocator: string;
  pnrStatus: BookingStatus;
  channel: BookingChannelType;
  gdsRecordLocator: string;
  flightJourneys: FlightJourney[];
  servicesPerPassenger: ServicesPerPassenger[];
  otherServices: SharedBookingServices[];
  paxContactInformation: ContactInformation | undefined;
  agencyContactInformation: ContactInformation | undefined;
  paymentDetails: PaymentDetails;
}
export interface PaymentDetails {
  journeys: JourneyPayment[] | undefined;
  payments: Payment[] | undefined;
  otherServices: PaymentElement[] | undefined;
  discounts: PaymentItem | undefined;
  totalPayment: number;
  amountDue: number;
  bookingTotalAmount: number;
  globalConcepts: PaymentItem[] | undefined;
}

declare interface JourneyPayment {
  total: number;
  bundleCode: string | undefined;
  fare: string | undefined;
  elements: PaymentElement[] | undefined;
  discounts: PaymentElement[] | undefined;
  taxes: PaymentItem[] | undefined;
  journeyDate: Date;
}

export interface PaymentItem {
  code: string;
  amount: number | undefined;
}

export interface PaymentElement {
  code: string;
  itemCount: number;
  itemAmount: number;
  isIncluded: boolean;
}

declare interface Payment {
  type: PaymentType;
  cardNumber: string | undefined;
  cardHolder: string | undefined;
  currencyCode: string | undefined;
  amount: number | undefined;
  paymentStatus: PaymentStatus;
}

export interface ContactInformation {
  agencyName: string | undefined;
  firstName: string | undefined;
  surname: string | undefined;
  phoneNumber: string | undefined;
  contactMail: string;
}

export interface ServicesPerPassenger {
  firstName: string;
  surname: string;
  outboundServices: Service[] | undefined;
  inboundServices: Service[] | undefined;
  outboundSeats: (string | undefined)[] | undefined;
  inboundSeats: (string | undefined)[] | undefined;
  hasInfant: boolean | undefined;
}

export interface Service {
  serviceName: string;
  count: number;
}

export interface FlightJourney {
  isNightFlight: boolean;
  isEarlyMorning: boolean;
  isPlusDay: boolean;
  connectionTime: string | undefined;
  connectionStation: string | undefined;
  totalTripTime: string | undefined;
  segments: Segment[];
}

export interface Segment {
  iataOrigin: string;
  departureDate: Date;
  terminalOrigin: string | undefined;
  iataDestination: string;
  arrivalDate: Date;
  terminalDestination: string | undefined;
  carrierName: AbbreviatedCarrierCodes;
  flightNumber: string;
  codeShareOperatorName: AbbreviatedCarrierCodes | undefined;
  codeShareFlightNumber: string | undefined;
  codeShareCarrierName: AbbreviatedCarrierCodes | undefined;
}

export interface IAlertConfig {
  enabled: boolean;
  title?: string;
  titleTag?: string;
  marginBottom: boolean;
  type: AlertMessageTypeEnum;
  size: AlertMessageSizeEnum;
  labelButton: string;
  buttonLink: string;
  attrTypeButton: AlertMessageAttrTypeEnum;
  messageButtonInline?: boolean;
  message: string;
}

export const AlertConfig: Record<AlertBookingStatus, IAlertConfig> = {
  Default: {
    enabled: false,
    type: AlertMessageTypeEnum.INFO,
    size: AlertMessageSizeEnum.S,
    attrTypeButton: AlertMessageAttrTypeEnum.LINK,
    marginBottom: true,
    labelButton: '',
    message: '',
    buttonLink: ''
  },
  PendingRefund: {
    enabled: true,
    type: AlertMessageTypeEnum.INFO,
    size: AlertMessageSizeEnum.S,
    attrTypeButton: AlertMessageAttrTypeEnum.LINK,
    marginBottom: true,
    labelButton: '',
    buttonLink: '',
    message: 'booking_details_component.alerts.pending_refund'
  },
  Refunded: {
    enabled: true,
    type: AlertMessageTypeEnum.INFO,
    size: AlertMessageSizeEnum.S,
    attrTypeButton: AlertMessageAttrTypeEnum.LINK,
    marginBottom: true,
    labelButton: '',
    buttonLink: '',
    message: 'booking_details_component.alerts.refunded'
  },
  PendingProcess: {
    enabled: true,
    type: AlertMessageTypeEnum.WARNING,
    size: AlertMessageSizeEnum.S,
    attrTypeButton: AlertMessageAttrTypeEnum.LINK,
    marginBottom: true,
    labelButton: '',
    buttonLink: '',
    message: 'booking_details_component.alerts.pending_process'
  },
  OperatedIberia: {
    enabled: true,
    type: AlertMessageTypeEnum.INFO,
    attrTypeButton: AlertMessageAttrTypeEnum.LINK,
    size: AlertMessageSizeEnum.M,
    marginBottom: true,
    labelButton: 'Iberia.com',
    buttonLink: 'https://www.iberia.com/',
    message: 'booking_details_component.alerts.gds_bookings_iberia'
  },
  OperatedBritish: {
    enabled: true,
    type: AlertMessageTypeEnum.INFO,
    attrTypeButton: AlertMessageAttrTypeEnum.LINK,
    size: AlertMessageSizeEnum.M,
    marginBottom: true,
    labelButton: 'britishairways.com',
    buttonLink: 'https://www.britishairways.com/',
    message: 'booking_details_component.alerts.gds_bookings_british'
  }
} as const;

export type BookingChannelType = 'B2B' | 'TTOO' | 'GDS';
export type AlertBookingStatus =
  | 'Default'
  | 'PendingProcess'
  | 'PendingRefund'
  | 'Refunded'
  | 'OperatedIberia'
  | 'OperatedBritish';
export type BookingStatus =
  | 'None'
  | 'PendingPayment'
  | 'PendingProcess'
  | 'PendingRefund'
  | 'PendingFlight'
  | 'Flown'
  | 'Cancelled'
  | 'Refunded'
  | 'PendingQuickFilters'
  | 'CanceledQuickFilters'
  | 'FlownQuickFilters';

export interface BookingDetailsViewModel {
  userName: string;
  currentLanguage: string;
  recordLocator: string;
  bookingDetails: BookingDetails;
  itineraryType: ItineraryTypeEnum;
  stations: Station[];
  breadcrumbItems: BreadcrumbItem[];
  featuredCards: FeaturedCard[];
  currencyCode: string | undefined;
  hasGcbg: boolean;
  isGds: boolean;
  isTtoo: boolean;
  areOtherManagementOptionsAllowed: boolean;
  areImproveTripOptionsAllowed: boolean;
  areJourneyDetailsAllowed: boolean;
}

export interface BookingDetailsSessionData {
  stations: Station[];
  language: string;
  user: string;
  recordLocator: string;
  email: string;
}
export interface FeaturedCard extends CardFeaturedModel {
  hasOutput: boolean;
  isAllowed: boolean;
  postActionRedirectUrl?: string;
  page?: AvailableWebsites;
}

export interface IBookingDetailsState {
  state: BookingDetailsSessionData;
  booking: BookingDetails;
}

export const emptyBookingDetailsState: BookingDetailsSessionData = {
  stations: [],
  language: '',
  user: '',
  recordLocator: '',
  email: ''
};

export const breadCrumbItems: BreadcrumbItem[] = [
  {
    label: 'header_component.links.home',
    routerLink: internalRoutes.Home
  },
  {
    label: 'home_component.cards.bookings',
    routerLink: internalRoutes.BookingList
  },
  {
    label: 'booking_details_component.flight_summary_header_component.title'
  }
];

export const featuredCards_Legacy: FeaturedCard[] = [
  {
    label: 'booking_details_component.other_management_options_component.check-in',
    icon: 'ds-icon-check-in-online',
    postActionRedirectUrl: redirectRoutes_Legacy.Checkin,
    hasOutput: false,
    isAllowed: false,
    page: 'Checkin'
  },
  {
    label: 'booking_details_component.other_management_options_component.change_seat',
    icon: 'ds-icon-seat',
    postActionRedirectUrl: redirectRoutes_Legacy.SeatChange,
    hasOutput: false,
    isAllowed: false,
    page: 'SeatChange'
  },
  {
    label: 'booking_details_component.other_management_options_component.flight_change',
    icon: 'ds-icon-plane-flight-change',
    postActionRedirectUrl: redirectRoutes_Legacy.FlightChange,
    hasOutput: false,
    isAllowed: false,
    page: 'FlightChange'
  },
  {
    label: 'booking_details_component.other_management_options_component.name_change',
    icon: 'ds-icon-change-name',
    postActionRedirectUrl: redirectRoutes_Legacy.NameChange,
    hasOutput: false,
    isAllowed: false,
    page: 'NameChange'
  },
  {
    label: 'booking_details_component.other_management_options_component.send_to_customer',
    icon: 'ds-icon-mail',
    hasOutput: true,
    isAllowed: true,
    page: 'BookingFlow'
  },
  {
    label: 'booking_details_component.other_management_options_component.api_transactions',
    postActionRedirectUrl: redirectRoutes_Legacy.UkTravel,
    icon: 'ds-icon-doc-edit',
    hasOutput: false,
    isAllowed: false,
    page: 'UkTravel'
  }
];

export const featuredCards: FeaturedCard[] = [
  {
    label: 'booking_details_component.other_management_options_component.check-in',
    icon: 'ds-icon-check-in-online',
    postActionRedirectUrl: redirectRoutes.Checkin,
    hasOutput: false,
    isAllowed: false,
    page: 'Checkin'
  },
  {
    label: 'booking_details_component.other_management_options_component.change_seat',
    icon: 'ds-icon-seat',
    postActionRedirectUrl: redirectRoutes.SeatChange,
    hasOutput: false,
    isAllowed: false,
    page: 'SeatChange'
  },
  {
    label: 'booking_details_component.other_management_options_component.flight_change',
    icon: 'ds-icon-plane-flight-change',
    postActionRedirectUrl: redirectRoutes.FlightChange,
    hasOutput: false,
    isAllowed: false,
    page: 'FlightChange'
  },
  {
    label: 'booking_details_component.other_management_options_component.name_change',
    icon: 'ds-icon-change-name',
    postActionRedirectUrl: redirectRoutes.NameChange,
    hasOutput: false,
    isAllowed: false,
    page: 'NameChange'
  },
  {
    label: 'booking_details_component.other_management_options_component.send_to_customer',
    icon: 'ds-icon-mail',
    hasOutput: true,
    isAllowed: true,
    page: 'BookingFlow'
  },
  {
    label: 'booking_details_component.other_management_options_component.api_transactions',
    postActionRedirectUrl: redirectRoutes.UkTravel,
    icon: 'ds-icon-doc-edit',
    hasOutput: false,
    isAllowed: false,
    page: 'UkTravel'
  }
];

export const englandCountryCode = 'GB';

export const emptyPreFooterConfig: IBookingDetailsState = {
  state: {
    recordLocator: '',
    stations: [],
    user: '',
    language: '',
    email: ''
  },
  booking: {
    paxAmount: 0,
    recordLocator: '',
    pnrStatus: 'None',
    channel: 'B2B',
    gdsRecordLocator: '',
    flightJourneys: [],
    servicesPerPassenger: [],
    otherServices: [],
    paxContactInformation: undefined,
    agencyContactInformation: undefined,
    paymentDetails: {
      journeys: undefined,
      payments: undefined,
      otherServices: undefined,
      discounts: undefined,
      totalPayment: 0,
      amountDue: 0,
      bookingTotalAmount: 0,
      globalConcepts: undefined
    }
  }
};

export const journeyDetailsDisallowed: BookingStatus[] = ['Cancelled', 'Refunded'];
export const otherManagementOptionsDisallowed: BookingStatus[] = [
  'PendingProcess',
  'PendingRefund',
  'Flown',
  'Cancelled',
  'Refunded'
];
export const improveTripOptionsDisallowed: BookingStatus[] = [
  'Cancelled',
  'Flown',
  'PendingPayment',
  'PendingProcess',
  'PendingRefund',
  'Refunded'
];

export const loaderPrimaryConfig: LoaderConfig = {
  accessibilityStatusText: 'ds.basket.loader.loading',
  isOpen: true,
  title: 'ds.basket.loader.loading',
  spinnerColor: SpinnerColorEnum.PRIMARY
};

export const configModalActionsConnectionError: ModalAction = {
  label: 'booking_details_component.error.return_start'
};

export const ConfigModalsOutput: ModalConfig = {
  title: 'booking_details_component.other_management_options_component.send_to_customer_component.title',
  content: GenericModalComponent,
  customData: {
    label: 'booking_details_component.other_management_options_component.send_to_customer_component.body',
    styles: 'bodyXL text-center'
  },
  primaryAction: {
    label: 'booking_details_component.other_management_options_component.send_to_customer_component.with_price',
    closeModal: true
  },
  secondaryAction: {
    label: 'booking_details_component.other_management_options_component.send_to_customer_component.without_price',
    type: ButtonTypeEnum.OUTLINE_SECONDARY,
    closeModal: true
  }
};

export const configModalsConnectionError: ModalConfig = {
  title: 'booking_details_component.error_modal.title',
  content: GenericModalComponent,
  customData: {
    label: 'booking_details_component.error_modal.content',
    styles: 'bodyXL text-center'
  },
  primaryAction: {
    label: 'booking_details_component.error_modal.return_button',
    closeModal: true
  },
  undismissable: true
};

export const CabinBagCodes = ['NGCB', 'GCBG'];
export const SegmentCheckCards = 'check-in';
